/* ============================================================
   /app/login and /app/welcome — magic-link landing.

   Both routes render this same component; "welcome" vs "login" is
   just copy (first-run invite vs. routine sign-in), the token
   verification is identical. If there's no ?t= token, falls back to
   the request form so a bookmarked /app/login still works.

   The 6-digit code is the primary path, not a fallback. An installed iOS
   home-screen app CANNOT be signed in by the emailed link: Mail opens
   links in Safari, and iOS gives the installed app a storage container
   separate from Safari's, so the link signs in the browser and the app
   keeps showing this screen forever. Typing the code is the only way the
   session lands in the app. The link still works for desktop and for
   anyone who never installed it.
   ============================================================ */
import { type FormEvent, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";

import { HttpError } from "../../lib/api";
import { requestLoginLink, verifyLoginCode, verifyLoginToken } from "../../lib/auth";

// Everyone's email is @sahabsystems.com — only ask for the local part so
// signing in at a conference booth is one field, not a full address to type
// (and typo) on a phone keyboard.
const EMAIL_DOMAIN = "sahabsystems.com";

type Stage =
  | { kind: "verifying" }
  | { kind: "verified" }
  | { kind: "needs-link" }
  | { kind: "code-sent" }
  | { kind: "error"; message: string };

export default function AppLogin({ variant }: { variant: "login" | "welcome" }) {
  const [params] = useSearchParams();
  const token = params.get("t");
  const [stage, setStage] = useState<Stage>(token ? { kind: "verifying" } : { kind: "needs-link" });
  const [localPart, setLocalPart] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const email = `${localPart.trim()}@${EMAIL_DOMAIN}`;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    verifyLoginToken(token)
      .then(() => {
        if (!cancelled) setStage({ kind: "verified" });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message =
          e instanceof HttpError
            ? "This link has expired or was already used. Request a new one below."
            : "Couldn't sign you in — check your connection and try again.";
        setStage({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onRequestLink(e: FormEvent) {
    e.preventDefault();
    if (!localPart.trim()) return;
    setSubmitting(true);
    try {
      await requestLoginLink(email);
      setCode("");
      setCodeError(null);
      setStage({ kind: "code-sent" });
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    if (code.trim().length !== 6) return;
    setSubmitting(true);
    setCodeError(null);
    try {
      await verifyLoginCode(email, code.trim());
      setStage({ kind: "verified" });
    } catch (err: unknown) {
      setCodeError(
        err instanceof HttpError && err.status === 429
          ? "Too many attempts. Request a new code in a few minutes."
          : "That code isn't right, or it's expired. Check the email or request a new code.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (stage.kind === "verified") {
    return <Navigate to="/app" replace />;
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-8 bg-paper">
      <div className="max-w-sm w-full text-center space-y-6">
        <h1 className="font-display text-2xl font-bold text-ink">
          sahab<span className="text-signal">.</span>
        </h1>

        {stage.kind === "verifying" && (
          <p className="text-ink/70">Signing you in…</p>
        )}

        {(stage.kind === "needs-link" || stage.kind === "error") && (
          <>
            {stage.kind === "error" && (
              <p className="text-sm text-red-600">{stage.message}</p>
            )}
            <p className="text-ink/70 text-sm">
              {variant === "welcome"
                ? "Enter your name to set up your card."
                : "Enter your name to sign in."}
            </p>
            <form onSubmit={onRequestLink} className="space-y-3">
              <div
                className="flex items-center rounded-lg border border-ink/15 bg-white
                           focus-within:ring-2 focus-within:ring-signal"
              >
                <input
                  type="text"
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value)}
                  placeholder="jafar"
                  className="min-w-0 flex-1 rounded-lg bg-transparent px-4 py-3 text-sm text-ink
                             placeholder:text-ink/40 focus:outline-none"
                />
                <span className="pr-4 text-sm text-ink/40 whitespace-nowrap">
                  @{EMAIL_DOMAIN}
                </span>
              </div>
              <button
                type="submit"
                disabled={submitting || !localPart.trim()}
                className="w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm
                           disabled:opacity-50"
              >
                {submitting ? "Sending…" : "Email me a sign-in code"}
              </button>
            </form>
          </>
        )}

        {stage.kind === "code-sent" && (
          <>
            <p className="text-ink/70 text-sm">
              If that address has an account, a 6-digit code is on its way to{" "}
              <span className="font-semibold text-ink">{email}</span>. Enter it below — it
              expires in 15 minutes and works once.
            </p>
            <form onSubmit={onSubmitCode} className="space-y-3">
              <input
                type="text"
                required
                // one-time-code lets iOS offer the code from the email above
                // the keyboard, so it can be filled without leaving the app.
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                aria-label="6-digit sign-in code"
                className="w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-center
                           font-mono text-2xl tracking-[.3em] text-ink placeholder:text-ink/25
                           focus:outline-none focus:ring-2 focus:ring-signal"
              />
              {codeError && <p className="text-sm text-red-600">{codeError}</p>}
              <button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm
                           disabled:opacity-50"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <button
              type="button"
              onClick={() => {
                setCode("");
                setCodeError(null);
                setStage({ kind: "needs-link" });
              }}
              className="text-sm text-ink/50 underline"
            >
              Use a different name
            </button>
            <p className="text-xs text-ink/40">
              The email also has a link, but if you opened Sahab vCard from your home screen,
              use the code — tapping the link signs you into your browser instead of the app.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
